import {
  ActionButtons,
  Badge,
  DataTable,
  EmptyState,
  type DataTableColumn,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as ViewIcon } from '@enamelai/design-system/icons/View'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { TransactionEntry } from '@services/api/billing/transaction.api'
import { formatCurrency, hasBillingListNarrowed } from '@utils/billing.utils'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useTransaction from './useTransaction'
import './TransactionTableStyle.css'

interface Props {
  searchQuery: string
  filters: Record<string, string[]>
  onView?: (row: TransactionEntry) => void
  onCreateInvoice?: () => void
  suppressTableLoading?: boolean
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const DEFAULT_SORT_COLUMN_ID = 'date'
const DEFAULT_SORT_DIRECTION: DataTableSortDirection = 'desc'

const transactionRowId = (entry: TransactionEntry, index: number): string =>
  `${index}-${entry.type}-${entry.date}`

const shouldDisableView = (row: TransactionEntry): boolean => {
  if (row.type === 'Billing' && row.description) {
    const description = row.description.toLowerCase()
    return description.includes('adjustment') || description.includes('payment')
  }
  return false
}

const formatAdjustment = (val: number | null): string => {
  if (val === null || val === 0) return ''
  if (val < 0) return `(${formatCurrency(Math.abs(val))})`
  return formatCurrency(val)
}

const formatAmount = (val: number | null): string =>
  val === null || val === 0 ? '' : formatCurrency(val)

const typeBadgeColor = (type: string): 'teal' | 'orange' | 'blue' | 'gray' => {
  if (type === 'Billing') return 'teal'
  if (type === 'Claim') return 'orange'
  if (type === 'Procedure') return 'blue'
  return 'gray'
}

const TransactionTable: React.FC<Props> = ({
  searchQuery,
  filters,
  onView,
  onCreateInvoice,
  suppressTableLoading = false,
}): JSX.Element => {
  const hooks = useTransaction(searchQuery, filters)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const isInitialLoadRef = useRef(true)
  const [sortColumnId, setSortColumnId] = useState(DEFAULT_SORT_COLUMN_ID)
  const [sortDirection, setSortDirection] = useState<DataTableSortDirection>(
    DEFAULT_SORT_DIRECTION,
  )

  useEffect(() => {
    if (hooks.loadingTransactions) {
      if (suppressTableLoading || isInitialLoadRef.current) return
      showLoading({
        container: '.transaction-table',
      })
      return
    }

    isInitialLoadRef.current = false
    hideLoading('.transaction-table')
  }, [
    hooks.loadingTransactions,
    showLoading,
    hideLoading,
    suppressTableLoading,
  ])

  const entriesById = useMemo(() => {
    const entries = new Map<string, TransactionEntry>()
    hooks.transactions.forEach((entry, index) => {
      entries.set(transactionRowId(entry, index), entry)
    })
    return entries
  }, [hooks.transactions])

  const rows = useMemo(
    (): DataTableRow[] =>
      hooks.transactions.map((entry, index) => ({
        id: transactionRowId(entry, index),
        date: entry.date,
        patient: entry.patient || '',
        description: entry.description || '',
        tooth_details: entry.tooth_details || '',
        charges: formatAmount(entry.charges),
        amount: formatAmount(entry.amount),
        insurance_adjustment: formatAdjustment(entry.insurance_adjustment),
        patient_adjustment: formatAdjustment(entry.patient_adjustment),
        balance: formatCurrency(entry.balance),
      })),
    [hooks.transactions],
  )

  const handleSort = useCallback(
    (columnId: string): void => {
      const nextDirection: DataTableSortDirection =
        sortColumnId === columnId && sortDirection === 'asc' ? 'desc' : 'asc'
      setSortColumnId(columnId)
      setSortDirection(nextDirection)
      hooks.handleTableSortChange({
        key: columnId,
        direction:
          nextDirection === 'asc' ? SortDirection.ASC : SortDirection.DESC,
      })
    },
    [hooks, sortColumnId, sortDirection],
  )

  const columns = useMemo((): DataTableColumn[] => {
    return [
      { id: 'date', label: 'Date', sortable: true, width: '100px' },
      { id: 'patient', label: 'Patient', sortable: true, width: '130px' },
      {
        id: 'type',
        label: 'Type',
        sortable: true,
        width: '95px',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          if (!entry) return null
          return (
            <Badge
              mode="tinted"
              color={typeBadgeColor(entry.type)}
              label={entry.type}
            />
          )
        },
      },
      {
        id: 'description',
        label: 'Description',
        sortable: false,
        width: '240px',
      },
      {
        id: 'tooth_details',
        label: 'T/S',
        sortable: false,
        width: '120px',
      },
      { id: 'charges', label: 'Charges', sortable: false, width: '100px' },
      { id: 'amount', label: 'Amount', sortable: false, width: '100px' },
      {
        id: 'insurance_adjustment',
        label: 'Insurance adj.',
        sortable: false,
        width: '120px',
      },
      {
        id: 'patient_adjustment',
        label: 'Patient adj.',
        sortable: false,
        width: '100px',
      },
      { id: 'balance', label: 'Balance', sortable: false, width: '100px' },
    ]
  }, [entriesById])

  const renderActions = (row: DataTableRow): React.ReactNode => {
    const entry = entriesById.get(row.id)
    if (!entry) return null
    const isDisabled = shouldDisableView(entry)

    return (
      <ActionButtons
        actions={[
          {
            id: 'view',
            label: 'View transaction',
            icon: (
              <ViewIcon className="ds-action-button__svg" aria-hidden="true" />
            ),
            tone: 'primary',
            variant: 'solid',
            disabled: isDisabled,
            onClick: () => {
              if (!isDisabled) onView?.(entry)
            },
          },
        ]}
      />
    )
  }

  const listNarrowed = hasBillingListNarrowed(searchQuery, filters)
  const showEmptyState =
    !hooks.loadingTransactions && hooks.transactions.length === 0
  const hasNoRecords = !listNarrowed
  const totalPages = Math.max(
    1,
    Math.ceil(hooks.transactionCount / hooks.pageSize),
  )

  if (showEmptyState) {
    return (
      <div className="billing-tab-panel billing-tab-panel--empty transaction-table">
        <EmptyState
          iconClass="mgc mgc_bill_2_fill"
          title="No transactions found"
          description={
            hasNoRecords
              ? "You don't have any transactions on record yet."
              : 'No transactions match your search.'
          }
          actionLabel={hasNoRecords ? 'Create Invoice' : undefined}
          onAction={hasNoRecords ? onCreateInvoice : undefined}
        />
      </div>
    )
  }

  return (
    <DataTable
      className="transaction-table"
      columns={columns}
      rows={rows}
      showActions
      actionsLabel="Actions"
      renderActions={renderActions}
      sortColumnId={sortColumnId}
      sortDirection={sortDirection}
      onSort={handleSort}
      pagination={{
        currentPage: hooks.currentPage,
        totalPages,
        pageSize: hooks.pageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        onPageChange: hooks.handlePageChange,
        onPageSizeChange: hooks.handlePageSizeChange,
      }}
    />
  )
}

export default TransactionTable
